import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import { createPaneDividerHost, PANE_DIVIDER_DRAGGING_CLASS, type TPaneDividerHost } from "../core/paneDivider"
import { resolveViewerPanes } from "../core/viewerPanes"
import { DEFAULT_SPLIT_FRACTION, MIN_PANE_WIDTH, resolveSplitFractionFromPointer } from "./paneDivider.helpers"

// Dragging the divider between the two split-screen panes — #1415.
//
// The node, the measurement and the published split are `core/paneDivider.ts`; the arithmetic is
// `paneDivider.helpers.ts` (pure, unit-tested directly). This hook is the behaviour in between: the
// pointer gesture, the per-frame publication, and holding each pane on the page the user was
// reading across the re-fit the width change triggers.
//
// Apryse's own divider is hidden by the stylesheet and its `onResize` is never called, so none of
// its clamps (`MIN_WIDTH = 350`, applied twice) apply any more — see core/paneDivider.ts for what
// exactly was wrong with them.

// How long to let the flex row and Apryse's re-fit settle after a drag before the page-anchor
// correction below. Matches useSplitScreen's own LAYOUT_SETTLE_MS / viewerPanes' — same animation.
const RESIZE_SETTLE_MS = 350

type TPageAnchor = { documentViewer: Core.DocumentViewer; page: number }

type Props = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	isInitialized: boolean
	// From splitScreen.store: our divider only exists while there are two panes to divide.
	isSplitActive: boolean
	// Bumped whenever Apryse re-mounts the document area — the signal to re-parent and re-bind.
	layoutEpoch: number
	getShadowRoot: () => ShadowRoot | undefined
}

export const usePaneDivider = ({ instanceRef, isInitialized, isSplitActive, layoutEpoch, getShadowRoot }: Props) => {
	const hostRef = useRef<TPaneDividerHost | null>(null)
	// Pane 1's share of the pane space. Survives a layoutEpoch bump (the node and the published
	// custom properties are ours) and resets to an even split when dual screen closes, so the next
	// entry always opens two equal panes (AC8).
	const fractionRef = useRef(DEFAULT_SPLIT_FRACTION)
	const isDraggingRef = useRef(false)
	// Whether this gesture has published anything yet. A press with no movement changes no width, so
	// nothing re-fits and there is nothing for the settle pass below to correct.
	const hasMovedRef = useRef(false)
	// Where the pointer landed relative to the handle's centre, held for the whole gesture: grabbing
	// the handle 5px off centre and moving 1px must move the split by 1px, not snap the handle under
	// the cursor by 5 (measured at 5.95px before this — "resizing from a place that doesn't
	// correspond to it" in miniature, which is the issue's own words).
	const grabOffsetRef = useRef(0)
	// Which page each pane was showing when the drag started — see settle() below.
	const pageAnchorsRef = useRef<TPageAnchor[]>([])
	const settleRafRef = useRef<number | null>(null)
	const settleTimerRef = useRef<number | null>(null)
	// Unbinds the "did anything else move a pane?" watch below; only live during a settle window.
	const settleWatchRef = useRef<(() => void) | null>(null)
	const isSettleAbandonedRef = useRef(false)

	const getHost = useCallback((): TPaneDividerHost => {
		if (!hostRef.current) hostRef.current = createPaneDividerHost()
		return hostRef.current
	}, [])

	// Tell both viewers their viewport moved. Apryse only re-renders pages at a new viewport when it
	// is told the scroll view changed (the same reason the middle dock's own effect does this), so
	// without it a pane keeps painting at its previous width until the next scroll or zoom.
	const syncPaneViewports = useCallback(() => {
		const viewers = instanceRef.current?.Core.getDocumentViewers?.() ?? []
		for (const viewer of viewers) viewer?.scrollViewUpdated?.()
	}, [instanceRef])

	const clearSettleTimers = useCallback(() => {
		if (settleRafRef.current != null) cancelAnimationFrame(settleRafRef.current)
		if (settleTimerRef.current != null) window.clearTimeout(settleTimerRef.current)
		settleRafRef.current = null
		settleTimerRef.current = null
		settleWatchRef.current?.()
		settleWatchRef.current = null
	}, [])

	// Drop a pending settle pass AND the anchors it would have applied. Every exit from the drag has
	// to come through here: a timer that outlives its own gesture applies a page anchor in the middle
	// of the NEXT one (two quick nudges of the split is normal use), and one that outlives split
	// screen itself calls `setCurrentPage` on a viewer `exitMultiViewerMode` has torn down.
	const cancelSettle = useCallback(() => {
		clearSettleTimers()
		pageAnchorsRef.current = []
		isSettleAbandonedRef.current = false
	}, [clearSettleTimers])

	// Watch for anything OTHER than the drag's own re-fit moving a pane, for the settle window only.
	// The anchor must lose to every one of these — see settle().
	const watchExternalPaneMovement = useCallback(
		(onMoved: () => void) => {
			const instance = instanceRef.current
			const unbinds: Array<() => void> = []
			for (const { scrollEl } of resolveViewerPanes(instance)) {
				// A wheel or a press inside a pane is the user's own input; nothing the re-fit does
				// emits either, so neither can be mistaken for the drift the anchor exists to correct.
				scrollEl.addEventListener("wheel", onMoved, { passive: true })
				scrollEl.addEventListener("pointerdown", onMoved)
				unbinds.push(() => {
					scrollEl.removeEventListener("wheel", onMoved)
					scrollEl.removeEventListener("pointerdown", onMoved)
				})
			}
			// A selection is what drives every centering call there is — a sidebar row click, and smart
			// navigation's auto-selected counterpart in the other pane. One listener covers both.
			const annotationManager = instance?.Core.documentViewer?.getAnnotationManager?.()
			if (annotationManager) {
				annotationManager.addEventListener("annotationSelected", onMoved)
				unbinds.push(() => annotationManager.removeEventListener("annotationSelected", onMoved))
			}
			return () => unbinds.forEach((unbind) => unbind())
		},
		[instanceRef],
	)

	// Both panes sit in a FIT zoom mode, so changing a pane's width makes Apryse re-fit it — which is
	// wanted here (the drawing must rescale to the pane it now has) but at the same scroll offset a
	// lower zoom also shows a DIFFERENT page, which is the #1311 viewport hazard `preservePaneViewport`
	// documents. So the re-fit is allowed to happen and the PAGE is anchored instead: whatever page a
	// pane was showing when the drag started, it is still showing when the drag settles (AC7).
	// `preservePaneViewport` itself is deliberately not used — it suppresses the re-fit altogether and
	// leaves the pane pinned to FitMode.Zoom for good, so the drawing would stop scaling with the pane.
	//
	// It is a CORRECTION, and it bails out rather than fights, because 350ms is inside human reaction
	// time: release the divider and immediately wheel-scroll, or click an eref with `Auto open floor
	// plan` on, and an unconditional anchor would yank the pane back — the exact conflict class
	// `core/viewerPanes.ts` records from the #1347 review, where a blind correction reverted a
	// correctly-centered eref click every time. So it applies only where the page ACTUALLY drifted, on
	// a viewer that is still live, and only while nothing else has moved a pane since the drag ended.
	const settle = useCallback(() => {
		clearSettleTimers()
		isSettleAbandonedRef.current = false
		settleWatchRef.current = watchExternalPaneMovement(() => {
			isSettleAbandonedRef.current = true
		})
		settleRafRef.current = requestAnimationFrame(() => {
			settleRafRef.current = null
			syncPaneViewports()
		})
		settleTimerRef.current = window.setTimeout(() => {
			settleTimerRef.current = null
			settleWatchRef.current?.()
			settleWatchRef.current = null
			syncPaneViewports()
			const anchors = pageAnchorsRef.current
			pageAnchorsRef.current = []
			if (isSettleAbandonedRef.current) return
			// Re-resolved, not remembered: `resolveViewerPanes` only returns a viewer whose scroll
			// element is still connected and whose document is still loaded, so this is also the
			// liveness check — `?.` guards a missing method, not a viewer Apryse has disposed.
			const liveViewers = new Set(
				resolveViewerPanes(instanceRef.current).map(({ documentViewer }) => documentViewer),
			)
			for (const { documentViewer, page } of anchors) {
				if (page <= 0) continue
				if (!liveViewers.has(documentViewer)) continue
				if (documentViewer.getCurrentPage?.() === page) continue
				documentViewer.setCurrentPage?.(page, false)
			}
		}, RESIZE_SETTLE_MS)
	}, [clearSettleTimers, instanceRef, syncPaneViewports, watchExternalPaneMovement])

	const endDrag = useCallback(
		(event: PointerEvent) => {
			if (!isDraggingRef.current) return
			isDraggingRef.current = false
			const element = hostRef.current?.element
			element?.classList.remove(PANE_DIVIDER_DRAGGING_CLASS)
			if (element?.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId)
			const hasMoved = hasMovedRef.current
			hasMovedRef.current = false
			// A press that published nothing changed no pane's width, so nothing re-fitted: skip the
			// settle pass entirely rather than scheduling a correction with nothing to correct.
			if (!hasMoved) {
				cancelSettle()
				return
			}
			settle()
		},
		[cancelSettle, settle],
	)

	const onPointerDown = useCallback(
		(event: PointerEvent) => {
			// Left button / primary contact only; anything else must stay available for a context menu.
			if (event.button !== 0) return
			const host = hostRef.current
			if (!host) return
			// A settle pass from the PREVIOUS gesture would otherwise fire mid-drag — visibly re-paging
			// both panes — and would consume this gesture's anchors on the way out.
			cancelSettle()
			isDraggingRef.current = true
			hasMovedRef.current = false
			const handleRect = host.element.getBoundingClientRect()
			grabOffsetRef.current = handleRect.width > 0 ? event.clientX - (handleRect.left + handleRect.width / 2) : 0
			pageAnchorsRef.current = resolveViewerPanes(instanceRef.current).map(({ documentViewer }) => ({
				documentViewer,
				page: documentViewer.getCurrentPage?.() ?? 0,
			}))
			// Capture so the gesture keeps tracking once the pointer leaves the 14px column — which it
			// does immediately — without listening on the whole document.
			host.element.setPointerCapture(event.pointerId)
			host.element.classList.add(PANE_DIVIDER_DRAGGING_CLASS)
			// The panes claim mousedown anywhere inside them (the #1119 hit-test bypass, "activate this
			// viewer", empty-space deselect); a press on the divider is none of those.
			event.preventDefault()
			event.stopPropagation()
		},
		[cancelSettle, instanceRef],
	)

	const onPointerMove = useCallback((event: PointerEvent) => {
		if (!isDraggingRef.current) return
		const host = hostRef.current
		if (!host) return
		// Measured every move, not cached at pointerdown: `measure()` reads the ROW (its left edge,
		// its width, the divider's own width and the sum of everything seated in it that is not a
		// pane's share) and never a pane's content width, so its output is invariant under the drag —
		// but the row itself is not. A panel that is still animating open when the handle is grabbed
		// (useSplitScreen waits LAYOUT_SETTLE_MS = 350 for exactly that animation) changes the row
		// mid-gesture, and a geometry cached at pointerdown would run the rest of the drag against a
		// row that no longer exists.
		const geometry = host.measure(MIN_PANE_WIDTH)
		if (!geometry) return
		// Nothing is scrolled here: the pointer maps straight to a fraction and the fraction is
		// published as two flex-grow values, so the browser lays the row out for the frame. That is
		// what keeps the handle under the cursor for the whole gesture (AC1) — and why this can run on
		// every move without a throttle.
		const fraction = resolveSplitFractionFromPointer(event.clientX - grabOffsetRef.current, geometry)
		if (fraction === fractionRef.current) return
		fractionRef.current = fraction
		hasMovedRef.current = true
		host.publishFraction(fraction)
	}, [])

	// Attach the divider into the pane row that exists NOW, re-publish the split, and (re)bind the
	// gesture. Re-runs on every layoutEpoch: entering/leaving split screen rebuilds `.MultiViewer`'s
	// children, and a divider left parented to the old row would silently vanish.
	useEffect(() => {
		if (!isInitialized) return
		const host = getHost()
		const attached = host.sync({ shadowRoot: getShadowRoot(), isSplitActive })
		if (!attached) {
			// Split screen just closed (or the row was rebuilt without one). Everything the gesture
			// owns has to be dropped here, not only on unmount: a pending settle would call
			// `setCurrentPage` on a viewer `exitMultiViewerMode` has disposed while `closeLeftView` is
			// re-applying the surviving pane's viewport (AC8), and the dragging class would ride on the
			// re-appended node into the next dual-screen session — where `endDrag` cannot remove it,
			// since it early-returns on a gesture that is no longer live, leaving the whole row stuck
			// on `cursor: col-resize`.
			cancelSettle()
			isDraggingRef.current = false
			hasMovedRef.current = false
			host.element.classList.remove(PANE_DIVIDER_DRAGGING_CLASS)
			fractionRef.current = DEFAULT_SPLIT_FRACTION
			host.publishFraction(DEFAULT_SPLIT_FRACTION)
			return
		}
		host.publishFraction(fractionRef.current)

		const element = host.element
		element.addEventListener("pointerdown", onPointerDown)
		element.addEventListener("pointermove", onPointerMove)
		element.addEventListener("pointerup", endDrag)
		element.addEventListener("pointercancel", endDrag)
		element.addEventListener("lostpointercapture", endDrag)
		return () => {
			element.removeEventListener("pointerdown", onPointerDown)
			element.removeEventListener("pointermove", onPointerMove)
			element.removeEventListener("pointerup", endDrag)
			element.removeEventListener("pointercancel", endDrag)
			element.removeEventListener("lostpointercapture", endDrag)
		}
	}, [
		isInitialized,
		isSplitActive,
		layoutEpoch,
		getShadowRoot,
		getHost,
		onPointerDown,
		onPointerMove,
		endDrag,
		cancelSettle,
	])

	// Viewer teardown: drop the timers and the node. The published custom properties go with the web
	// component itself, so there is nothing else to clean up.
	useEffect(() => {
		return () => {
			cancelSettle()
			hostRef.current?.destroy()
			hostRef.current = null
		}
	}, [cancelSettle])
}
